/* eslint-disable default-case */
import { useMutation, useQuery } from "@apollo/client";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useFormik } from "formik";
import _ from "lodash";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";

import { GridInputs, Tabs } from "../../../../components/Functional";
import { Button } from "../../../../components/ui";
import { LIST_TYPES } from "../../../../constants/listTypes";
import {
  CREATE_CONTACT_LIST_OFFERS as createContactListOffersMutation,
  CREATE_CONTACT_OFFERS as createContactOffersMutation,
} from "../../../../graphql/mutation/Offer";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";

const CreateOffersBody = (props = {}) => {
  const { closeModal, ids: offerIds, listId, search, select } = props;
  const [warnings, setWarnings] = useState([]);
  const [createdOffers, setCreatedOffers] = useState([]);
  const [updatedOffers, setUpdatedOffers] = useState([]);
  const { data } = useQuery(fetchLists({}), {
    variables: { input: { typeId: LIST_TYPES.CONTACT } },
  });
  const contactLists = data?.lists?.edges ?? [];

  const [createContactListOffers, listMutationContext] = useMutation(
    createContactListOffersMutation({
      id: true,
      contact: { id: true, name: true },
    }),
    { refetchQueries: ["FetchOffersV2", "FetchListItems", "FetchOffersList"] },
  );
  const [createContactOffers, mutationContext] = useMutation(
    createContactOffersMutation({
      id: true,
      contact: { id: true, name: true },
    }),
    { refetchQueries: ["FetchOffersV2", "FetchListItems", "FetchOffersList"] },
  );

  const loading = listMutationContext.loading || mutationContext.loading;
  const reset = () => {
    listMutationContext.reset();
    mutationContext.reset();
  };

  const { groupedCreatedOffers, groupedUpdatedOffers } = useMemo(() => {
    const groupedCreated = _.groupBy(createdOffers, "contact.name");
    const groupedUpdated = _.groupBy(updatedOffers, "contact.name");

    return {
      groupedCreatedOffers: groupedCreated,
      groupedUpdatedOffers: groupedUpdated,
    };
  }, [createdOffers, updatedOffers]);

  const formik = useFormik({
    initialValues: {
      ...(select === "all"
        ? { ...(listId ? { listId } : { search }) }
        : { offerIds }),
      contactListId: null,
      contactId: null,
      tab: "contact",
    },
    enableReinitialize: true,
    validationSchema: () => {
      switch (formik?.values?.tab) {
        case "contact":
          return Yup.object({
            contactId: Yup.number()
              .required("Please select a contact.")
              .typeError("Please select a contact."),
          });
        case "contactList":
          return Yup.object({
            contactListId: Yup.number()
              .required("Please select a contact list.")
              .typeError("Please select a contact list."),
          });
      }
    },
    onSubmit: async values => {
      let result;

      switch (values.tab) {
        case "contact": {
          const { contactId, offerIds, listId } = values;

          const response = await createContactOffers({
            variables: {
              input: { contactId, offerIds, listId },
            },
          });

          result = response?.data?.createContactOffers;
          break;
        }
        case "contactList": {
          const { contactListId, offerIds, listId } = values;

          const response = await createContactListOffers({
            variables: {
              input: { contactListId, offerIds, listId },
            },
          });

          result = response.data.createContactListOffers;
          break;
        }
      }

      if (result?.success) {
        const warnings = result?.warnings;
        setWarnings(() => warnings);
        setCreatedOffers(() => result.created);
        setUpdatedOffers(() => result.updated);
        reset();
        formik?.resetForm();
      }
    },
  });

  const contactProps = {
    formik,
    className: "grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8",
    inputs: [
      {
        label: "Contact Search",
        name: "contactId",
        type: "contactSearch",
      },
    ],
  };

  const contactListProps = {
    formik,
    className: "grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8",
    inputs: [
      {
        label: "Contact List",
        name: "contactListId",
        type: "multi-select",
        options: contactLists?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
    ],
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Contact",
        value: "contact",
        component: <GridInputs {...contactProps} />,
      },
      {
        label: "Contact List",
        value: "contactList",
        component: <GridInputs {...contactListProps} />,
      },
    ],
  };

  // API call succeeded
  if (createdOffers.length || updatedOffers.length || warnings.length) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <XMarkIcon
          className={`text-black-400 absolute right-3 top-3 h-4 w-4 cursor-pointer`}
          onClick={() => {
            setWarnings(() => []);
            setCreatedOffers(() => []);
            setUpdatedOffers(() => []);
            closeModal();
          }}
        />
        <div>
          {createdOffers.length ? (
            <div className="flex flex-col">
              <div className="m-2 text-2xl font-bold">
                Created {createdOffers.length} offers
              </div>
              <div>
                {Object.keys(groupedCreatedOffers).map((contactName, index) => {
                  const created = groupedCreatedOffers[contactName];
                  const {
                    id: offerId,
                    contact: { id: contactId },
                  } = created[0];

                  return (
                    <div key={index + offerId} className="py-2">
                      <Link
                        className="inline px-1 underline"
                        to={`/contacts/${contactId}/offers`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {contactName}
                      </Link>
                      - {created.map(offer => offer.id).join(" ")}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          {updatedOffers.length ? (
            <div className="flex flex-col">
              <div className="m-2 text-2xl font-bold">
                Updated {updatedOffers.length} offers
              </div>
              <div>
                {Object.keys(groupedUpdatedOffers).map((contactName, index) => {
                  const updated = groupedUpdatedOffers[contactName];
                  const {
                    id: offerId,
                    contact: { id: contactId },
                  } = updated[0];

                  return (
                    <div key={index + offerId} className="py-2">
                      <Link
                        className="inline px-1 underline"
                        to={`contacts/${contactId}/offers`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {contactName}
                      </Link>
                      - {updated.map(offer => offer.id).join(" ")}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          {warnings.length ? (
            <div className="flex flex-col">
              <div className="flex flex-col">
                <div className="m-2 text-2xl font-bold">Warnings</div>
                <div className="flex flex-col">
                  {warnings.map((warning, index) => {
                    const { message } = warning;

                    return (
                      <div key={"warn" + index} className="py-2">
                        <span>{message}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          Create Offers
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          <Button
            action="default"
            className="sm:mr-4"
            label="Cancel"
            onClick={() => {
              closeModal();
              formik?.resetForm();
            }}
          />
          <Button
            label={loading ? "Creating" : "Create"}
            disabled={loading || !formik?.isValid}
            onClick={formik.submitForm}
          />
        </div>
      </div>
      <div className="mt-8 pb-8">
        <Tabs {...tabsProps} />
      </div>
    </div>
  );
};

export default CreateOffersBody;
